// Пакет B «Платежи» (31.10.2026): возвраты через API провайдера (fd-order-18)
// и разделённые платежи (ТЗ-1.2) — /admin/payment-refunds/*, /admin/payment-splits/*.
//
// Свой маленький клиент раздела, как у клиентов продавцов (seller-clients-api.ts):
// данные есть только на шлюзе, общий интерфейс Api консоли и его мок не трогаем.
import { ApiClientError, createApiClient } from "@osmix/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/errors";
import { getAuthToken } from "@/api/token";

const readEnv = (read: () => string | undefined): string | undefined => {
	try {
		return read();
	} catch {
		return undefined;
	}
};

// Литерал целиком: Bun подставляет BUN_PUBLIC_* при сборке только так.
const GATEWAY_URL = readEnv(() => process.env.BUN_PUBLIC_API_GATEWAY_URL) ?? "";

export const paymentsAdminAvailable = Boolean(GATEWAY_URL);

const client = GATEWAY_URL ? createApiClient({ baseUrl: GATEWAY_URL, getBearerToken: getAuthToken }) : null;

type Envelope<T> = { success: true; data: T } | { success: false; error?: { code?: string; message?: string } };

const call = async <T>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> => {
	if (!client) throw new ApiError("Раздел доступен только при подключении к шлюзу", 503);
	try {
		const payload = await client.request<Envelope<T>>(path, opts);
		if (payload == null) return null as T;
		if (payload.success) return payload.data;
		throw new ApiError(payload.error?.message ?? "Request failed", 400, payload.error?.code);
	} catch (error) {
		if (error instanceof ApiError) throw error;
		if (error instanceof ApiClientError) {
			const body = error.body as Envelope<T> | null;
			const err = body && body.success === false ? body.error : undefined;
			throw new ApiError(err?.message ?? `Request failed: ${error.status}`, error.status, err?.code);
		}
		throw error;
	}
};

export type PaymentRefundStatus = "queued" | "processing" | "succeeded" | "failed" | "uncertain" | "manual";

export type PaymentRefund = {
	id: string;
	kind: "order_cancel" | "return" | "stale";
	provider: string;
	orderId: string;
	returnId?: string;
	amount: number;
	paidAmount: number;
	full: boolean;
	status: PaymentRefundStatus;
	providerRequestId?: string;
	providerState?: string;
	attempts: number;
	lastError?: string;
	nextAttemptAt?: string;
	createdAt: string;
	updatedAt: string;
	completedAt?: string;
};

export type SplitAccount = {
	shopId: string;
	provider: string;
	merchantId: string;
	status: "active" | "disabled";
	note: string;
	updatedAt: string;
};

export type SplitStatus = {
	enabled: boolean;
	provider: string;
	platformMerchantId: string;
	accounts: SplitAccount[];
	settledToShops: number;
};

/** Подпись статуса возврата для консоли — коротко и по делу. */
export const refundStatusLabel: Record<PaymentRefundStatus, string> = {
	queued: "В очереди на отправку",
	processing: "Принят Робокассой, исполняется",
	succeeded: "Деньги вернулись покупателю",
	failed: "Не прошёл",
	uncertain: "Нет ответа - проверьте в кабинете",
	manual: "Проведён вручную",
};

export const usePaymentRefunds = (orderId: string | undefined) =>
	useQuery({
		queryKey: ["admin", "payment-refunds", orderId ?? "open"],
		queryFn: () =>
			call<{ enabled: boolean; items: PaymentRefund[] }>(
				orderId ? `/admin/payment-refunds?orderId=${encodeURIComponent(orderId)}` : "/admin/payment-refunds",
			),
		enabled: paymentsAdminAvailable,
	});

export const usePaymentRefundAction = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, action }: { id: string; action: "retry" | "manual" }) =>
			call<PaymentRefund>(`/admin/payment-refunds/${encodeURIComponent(id)}/${action}`, { method: "POST" }),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ["admin", "payment-refunds"] });
			void qc.invalidateQueries({ queryKey: ["orders"] });
			void qc.invalidateQueries({ queryKey: ["returns"] });
		},
	});
};

export const useSplitStatus = () =>
	useQuery({
		queryKey: ["admin", "payment-splits"],
		queryFn: () => call<SplitStatus>("/admin/payment-splits"),
		enabled: paymentsAdminAvailable,
	});

export const useSplitAccountSave = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (input: { shopId: string; merchantId: string; status?: "active" | "disabled" }) =>
			call<SplitAccount>(`/admin/payment-splits/accounts/${encodeURIComponent(input.shopId)}`, {
				method: "PUT",
				body: { merchantId: input.merchantId, ...(input.status ? { status: input.status } : {}) },
			}),
		onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "payment-splits"] }),
	});
};

export const useSplitAccountRemove = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (shopId: string) =>
			call<null>(`/admin/payment-splits/accounts/${encodeURIComponent(shopId)}`, { method: "DELETE" }),
		onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "payment-splits"] }),
	});
};
